import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { Clapperboard, Film, Radio, RefreshCw, Trophy, Tv } from 'lucide-react';

const EVERY_MS = 10_000
const SHOWN = 10 // par liste ; « voir plus » pour le reste
const KINDS = {
    film: { label: 'Films', one: 'film', color: '#9754e3' },
    serie: { label: 'Séries', one: 'série', color: '#38bdf8' },
    sport: { label: 'Sport', one: 'lecture', color: '#22c55e' },
}

/** Une liste (films, séries ou sport) : les 10 plus regardés, « Voir les N autres » / « Voir moins ». */
const LiveList = ({ kind, icon: Icon, title, items, empty }) => {
    const [all, setAll] = useState(false)
    const shown = all ? items : items.slice(0, SHOWN)
    return (
        <div className={`up-live-list ${kind}`}>
            <p className='up-live-list-title'><Icon size={16} /> {title} <span>{items.length ? `· ${items.length}` : ''}</span></p>
            {!items.length && <p className='up-empty'>{empty}</p>}
            {shown.map((t, i) => (
                <div key={t.contentId ?? t.title} className='up-live-row'>
                    <span className='up-live-rank'>{i + 1}</span>
                    {kind === 'sport'
                        ? <span className='up-live-ph sport'><Tv size={16} /></span>
                        : t.poster ? <img src={t.poster} alt='' loading='lazy' /> : <span className='up-live-ph'><Film size={16} /></span>}
                    <div className='up-live-main'>
                        <b>{t.title}</b>
                        <small>
                            {kind === 'sport'
                                ? t.detail || 'Sport'
                                : <>{t.year || '—'}{kind === 'serie' && t.episodes > 0 ? ` · ${t.episodes} épisode${t.episodes > 1 ? 's' : ''} en cours` : ''}</>}
                        </small>
                    </div>
                    <span className={`up-live-n ${kind}`} title='Lectures en cours'>{t.viewers}</span>
                </div>
            ))}
            {items.length > SHOWN && (
                <button className='up-live-more' onClick={() => setAll((v) => !v)}>
                    {all ? 'Voir moins' : `Voir les ${items.length - SHOWN} autres`}
                </button>
            )}
        </div>
    )
}

/**
 * « En direct » : ce que les gens regardent en ce moment (traceur des lecteurs, GET /watching/live) — films, séries et
 * sport à part, + courbe des 60 dernières minutes. Relu toutes les 10 s tant que l'onglet est affiché.
 * Projet sans cette route : rien n'est affiché.
 */
const UpLive = ({ pApi }) => {
    const [data, setData] = useState(null)
    const [missing, setMissing] = useState(false)
    const [busy, setBusy] = useState(false)

    const load = useCallback(async () => {
        setBusy(true)
        try {
            setData(await pApi.get('/watching/live'))
            setMissing(false)
        } catch (e) { if (/introuvable|404/i.test(e.message)) setMissing(true) }
        setBusy(false)
    }, [pApi])
    useEffect(() => {
        load()
        const t = setInterval(() => document.visibilityState === 'visible' && load(), EVERY_MS)
        return () => clearInterval(t)
    }, [load])

    if (missing) return null
    if (!data) return <div className='up-live skeleton'></div>
    const { total, films, series, sport, devices, history } = data
    const counts = [
        ['film', Film, films, `${films.titles.length} titre${films.titles.length > 1 ? 's' : ''}`],
        ['serie', Clapperboard, series, `${series.titles.length} série${series.titles.length > 1 ? 's' : ''}`],
        ['sport', Trophy, sport, `${sport.titles.length} match${sport.titles.length > 1 ? 's' : ''} / chaîne${sport.titles.length > 1 ? 's' : ''}`],
    ]

    return (
        <section className='up-live'>
            <header className='up-live-head'>
                <h2><span className='up-live-dot' /> En direct</h2>
                <span className='up-muted'>
                    {total.plays} lecture{total.plays > 1 ? 's' : ''} · {total.users} compte{total.users > 1 ? 's' : ''}
                    {devices.length > 0 && <> · {devices.map((d) => `${d.type} ${d.plays}`).join(' · ')}</>}
                </span>
                <button className='pay-act-btn' onClick={load} title='Relire maintenant' disabled={busy}>
                    <RefreshCw size={15} color='#fff' className={busy ? 'up-spin' : ''} />
                </button>
            </header>

            <div className='up-live-counts'>
                {counts.map(([kind, Icon, d, sub]) => (
                    <div key={kind} className={`up-live-count ${kind}`}>
                        <span><Icon size={18} /> {KINDS[kind].label}</span>
                        <b>{d.plays}</b>
                        <small>{sub}</small>
                    </div>
                ))}
                <div className='up-live-chart'>
                    <p className='up-chart-title'>Comptes en lecture <span>· 60 dernières minutes</span></p>
                    <ResponsiveContainer width='100%' height={92}>
                        <AreaChart data={history} margin={{ top: 4, right: 14, left: 14, bottom: 0 }}>
                            <XAxis dataKey='at' tickFormatter={(t) => moment(t).format('HH:mm')} tick={{ fill: '#8f909c', fontSize: 10 }} axisLine={false} tickLine={false} interval={14} />
                            <Tooltip
                                contentStyle={{ background: '#1b1822', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 12 }}
                                labelFormatter={(t) => moment(t).format('HH:mm')}
                                formatter={(v, k) => [v, KINDS[k]?.label || k]}
                            />
                            {Object.entries(KINDS).map(([k, { color }]) => (
                                <Area key={k} type='monotone' dataKey={k} stackId='1' stroke={color} fill={color} fillOpacity={0.35} isAnimationActive={false} />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className='up-live-lists'>
                <LiveList kind='film' icon={Film} title='Films regardés' items={films.titles} empty='Personne ne regarde de film' />
                <LiveList kind='serie' icon={Clapperboard} title='Séries regardées' items={series.titles} empty='Personne ne regarde de série' />
                <LiveList kind='sport' icon={Radio} title='Sport en direct' items={sport.titles} empty='Personne ne regarde de sport' />
            </div>
        </section>
    )
}

export default UpLive;
